// Browser side of device notifications: permission, service worker and push subscription.
import { api } from './api.ts';

export type PushSupport = 'supported' | 'unsupported' | 'needs-install';

/** iPhone/iPad Safari only allows web push for apps added to the home screen. */
export function pushSupport(): PushSupport {
  const hasApi = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (hasApi) return 'supported';
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const standalone = window.matchMedia('(display-mode: standalone)').matches;
  return ios && !standalone ? 'needs-install' : 'unsupported';
}

function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded = (value + '='.repeat((4 - (value.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

async function registration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration('/');
  return existing ?? navigator.serviceWorker.register('/sw.js', { scope: '/' });
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (pushSupport() !== 'supported') return null;
  const reg = await navigator.serviceWorker.getRegistration('/');
  return reg ? reg.pushManager.getSubscription() : null;
}

/** Asks permission (must be called from a click), subscribes and registers this device. */
export async function enablePush(): Promise<'enabled' | 'denied' | 'unsupported'> {
  if (pushSupport() !== 'supported') return 'unsupported';
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return 'denied';
  const reg = await registration();
  await navigator.serviceWorker.ready;
  const { publicKey } = await api.get<{ publicKey: string }>('/api/push/public-key');
  let sub = await reg.pushManager.getSubscription();
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: base64UrlToBytes(publicKey) });
  await api.post('/api/me/push', sub.toJSON());
  return 'enabled';
}

export async function disablePush(): Promise<void> {
  const sub = await currentSubscription();
  if (!sub) return;
  await api.del('/api/me/push', { endpoint: sub.endpoint }).catch(() => undefined);
  await sub.unsubscribe();
}

/** Keeps the server's copy of this device's subscription fresh (endpoints can rotate). */
export async function syncPushSubscription(): Promise<void> {
  if (pushSupport() !== 'supported' || Notification.permission !== 'granted') return;
  const sub = await currentSubscription();
  if (sub) await api.post('/api/me/push', sub.toJSON()).catch(() => undefined);
}
